import { useEffect, useState, useCallback, useMemo, Fragment } from 'react';
import { RefreshCw, Key, X, AlertCircle, Play, Plus, Trash2, KeyRound, Unlink, Search, ChevronDown, ChevronRight, Download, CheckCircle, Info, Filter, RotateCcw, Bug, Building2 } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { RegistrarAccount, RegistrarType, SyncLog, ApiResponse, ScanResult, DiscoveredRegistrarItem, OnePasswordStatus } from '../types';
import { logoUrlFor } from '../lib/registrar-logos';
import { registrarName } from '../lib/registrar-names';
import { runSync, formatSyncOutcome } from '../lib/sync';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { useToast } from '../components/ui/Toast';

// What optional credential fields are for, shown under the input.
const OPTIONAL_FIELD_HINTS: Record<string, string> = {
  shopper_id: 'Your GoDaddy customer number (account menu). Needed to renew domains from the app; not needed for sync. In 1Password, add a field labelled "shopper_id" or "Customer Number".',
};

// Required fields per registrar for API access
const REQUIRED_FIELDS: Record<string, string[]> = {
  godaddy: ['api_key', 'api_secret'],
  namecheap: ['api_key', 'username', 'client_ip'],
  dynadot: ['api_key'],
  gandi: ['api_key'],
  dreamhost: ['api_key'],
  sedo: ['username', 'password'],
  porkbun: ['api_key', 'api_secret'],
  namesilo: ['api_key'],
  sav: ['api_key'],
  namebright: ['client_id', 'client_secret'],
  ilkari: ['api_key', 'tenant_id'],
  safenames: ['api_username', 'api_password'],
};

export default function RegistrarsPage() {
  const { fetchApi } = useApi();
  const confirm = useConfirm();
  const toast = useToast();
  const [accounts, setAccounts] = useState<RegistrarAccount[]>([]);
  const [registrarTypes, setRegistrarTypes] = useState<RegistrarType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState<RegistrarAccount | null>(null);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<Record<string, SyncLog>>({});

  // Filter state
  const [filterText, setFilterText] = useState('');

  // 1Password scan state
  const [opStatus, setOpStatus] = useState<OnePasswordStatus | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [expandedRegistrars, setExpandedRegistrars] = useState<Set<string>>(new Set());
  const [linking, setLinking] = useState<string | null>(null);

  // Filtered accounts
  const filteredAccounts = useMemo(() => {
    if (!filterText.trim()) return accounts;
    const search = filterText.toLowerCase();
    return accounts.filter(account =>
      account.label.toLowerCase().includes(search) ||
      (account.display_name ?? '').toLowerCase().includes(search) ||
      account.registrar_name.toLowerCase().includes(search) ||
      (account.username_hint?.toLowerCase().includes(search)) ||
      account.id.toLowerCase().includes(search)
    );
  }, [accounts, filterText]);

  // Group filtered accounts by registrar_type so the list reads as a series
  // of per-registrar cards instead of a single flat table. Each group sorts
  // its accounts alphabetically by label; groups are sorted by registrar
  // display name.
  const groupedAccounts = useMemo(() => {
    const map = new Map<string, { type: string; name: string; accounts: RegistrarAccount[] }>();
    for (const acc of filteredAccounts) {
      const key = acc.registrar_type;
      let group = map.get(key);
      if (!group) {
        group = { type: key, name: acc.registrar_name, accounts: [] };
        map.set(key, group);
      }
      group.accounts.push(acc);
    }
    for (const g of map.values()) {
      g.accounts.sort((a, b) => (a.display_name ?? a.label).localeCompare(b.display_name ?? b.label));
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredAccounts]);

  useEffect(() => {
    loadOnePasswordStatus();
  }, []);

  useEffect(() => {
    loadAccounts();
    loadRegistrarTypes();
    loadSyncStatus();
  }, []);

  async function loadAccounts() {
    try {
      const res = await fetchApi<ApiResponse<RegistrarAccount[]>>('/accounts');
      if (res.data) setAccounts(res.data);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadRegistrarTypes() {
    try {
      const res = await fetchApi<ApiResponse<RegistrarType[]>>('/accounts/types');
      if (res.data) setRegistrarTypes(res.data);
    } catch (error) {
      console.error('Failed to load registrar types:', error);
    }
  }

  async function loadSyncStatus() {
    try {
      const res = await fetchApi<ApiResponse<Record<string, SyncLog>>>('/accounts/sync-status');
      if (res.data) setSyncStatus(res.data);
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  }

  async function loadSyncLogs(accountId: string) {
    try {
      const res = await fetchApi<ApiResponse<SyncLog[]>>(
        `/accounts/${accountId}/sync-logs`
      );
      if (res.data) setSyncLogs(res.data);
    } catch (error) {
      console.error('Failed to load sync logs:', error);
    }
  }

  const handleSync = async (accountId: string) => {
    setSyncing(accountId);
    try {
      const outcome = await runSync(fetchApi, accountId);
      // Completed → success; failed/stalled → error; still running → info.
      const msg = formatSyncOutcome(outcome);
      if (outcome.status === 'completed') toast.success('Sync complete', msg);
      else if (outcome.status === 'failed' || outcome.status === 'stalled') toast.error('Sync failed', msg);
      else toast.info('Sync still running', msg);
      loadAccounts();
      loadSyncStatus();
      if (selectedAccount?.id === accountId) {
        loadSyncLogs(accountId);
      }
    } catch (error) {
      toast.error('Sync failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setSyncing(null);
    }
  };

  const handleSyncAll = async () => {
    const syncable = accounts.filter(a =>
      a.has_api && (a.credential_source === '1password' ? a.api_ready : a.has_credentials)
    );
    if (syncable.length === 0) {
      toast.info('Nothing to sync', 'No accounts with API credentials to sync.');
      return;
    }
    if (!(await confirm({
      title: `Sync ${syncable.length} connected account${syncable.length > 1 ? 's' : ''}?`,
      message: 'Each account with API credentials is synced in turn.',
      confirmLabel: 'Sync',
    }))) return;

    setSyncingAll(true);
    let succeeded = 0;
    let failed = 0;

    for (const account of syncable) {
      setSyncing(account.id);
      try {
        const outcome = await runSync(fetchApi, account.id);
        if (outcome.status !== 'completed') throw new Error(formatSyncOutcome(outcome));
        succeeded++;
      } catch {
        failed++;
      }
    }

    setSyncing(null);
    setSyncingAll(false);
    loadAccounts();
    loadSyncStatus();
    if (failed > 0) toast.error('Sync finished with failures', `${succeeded} succeeded, ${failed} failed`);
    else toast.success('Sync complete', `${succeeded} succeeded, ${failed} failed`);
  };

  const handleTest = async (accountId: string) => {
    setTesting(accountId);
    try {
      const res = await fetchApi<ApiResponse<{ message: string }>>(
        `/accounts/${accountId}/test`,
        { method: 'POST' }
      );
      toast.success('Connection test passed', res.data?.message || 'Connection successful');
    } catch (error) {
      toast.error('Connection test failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setTesting(null);
    }
  };

  // TEMPORARY: Safenames /token diagnostic. Runs the probe from the app's
  // whitelisted IP with the API creds and a portal-creds fallback, surfacing
  // the raw status + body so we can pin down the server-side 500.
  const handleSafenamesProbe = async (accountId: string) => {
    type ProbeResponse = {
      used_field?: string;
      masked_username?: string;
      credential_keys_present?: string[];
      response?: {
        status?: number; status_text?: string; content_type?: string | null;
        location?: string | null; token?: string | null; body_snippet?: string;
      };
    };
    setTesting(accountId);
    try {
      const run = async (payload: Record<string, unknown>, label: string) => {
        try {
          const res = await fetchApi<ApiResponse<ProbeResponse>>(
            `/accounts/${accountId}/safenames-token-probe`,
            { method: 'POST', body: JSON.stringify(payload) },
          );
          return { label, data: res.data };
        } catch (e) {
          return { label, error: e instanceof Error ? e.message : 'error' };
        }
      };
      const variants = [
        await run({}, 'API creds'),
        await run({ use_portal: true }, 'Portal creds'),
      ];
      const fmt = (v: { label: string; data?: ProbeResponse; error?: string }) => {
        if (v.error || !v.data) return `[${v.label}] error: ${v.error || 'no data'}`;
        const r = v.data.response || {};
        const outcome = r.token ? `token ${r.token}` : (r.body_snippet || '').slice(0, 100);
        return `[${v.label}] used=${v.data.used_field}, user=${v.data.masked_username}\n  → HTTP ${r.status} ${r.status_text || ''}\n  ${outcome}`;
      };
      const detected = variants[0]?.data?.credential_keys_present?.join(', ') || '?';
      toast.info('Safenames /token probe', `(detected creds: ${detected})\n\n${variants.map(fmt).join('\n\n')}\n\nFull JSON logged to console.`);
      console.log('[Safenames probe]', variants);
    } finally {
      setTesting(null);
    }
  };

  const handleDelete = async (accountId: string) => {
    if (!(await confirm({
      title: 'Delete this account?',
      message: 'Are you sure you want to delete this account?',
      tone: 'danger',
    }))) return;

    try {
      await fetchApi(`/accounts/${accountId}`, { method: 'DELETE' });
      loadAccounts();
      if (selectedAccount?.id === accountId) {
        setSelectedAccount(null);
      }
    } catch (error) {
      toast.error('Delete failed', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const handleUnlink = async (accountId: string) => {
    if (!(await confirm({
      title: 'Unlink from 1Password?',
      message: 'This will unlink the account from 1Password. You will need to set local credentials afterwards.',
      confirmLabel: 'Unlink',
      tone: 'danger',
    }))) return;

    try {
      await fetchApi(`/1password/accounts/${accountId}/unlink`, { method: 'POST' });
      toast.success('Account unlinked from 1Password', 'Please set local credentials.');
      loadAccounts();
      if (selectedAccount?.id === accountId) {
        const res = await fetchApi<ApiResponse<RegistrarAccount>>(`/accounts/${accountId}`);
        if (res.data) setSelectedAccount(res.data);
      }
    } catch (error) {
      toast.error('Unlink failed', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const handleRefresh1Password = async (accountId: string) => {
    setRefreshing(accountId);
    try {
      type Validation =
        | { status: 'skipped_no_fields' }
        | { status: 'skipped_no_adapter' }
        | { status: 'validated'; message: string }
        | { status: 'failed'; message: string }
        | { status: 'archived' }
        | { status: 'item_missing' };
      const res = await fetchApi<ApiResponse<{
        account: RegistrarAccount;
        detected_fields: string[];
        api_ready: boolean;
        validation: Validation;
        is_active?: number;
      }>>(`/1password/accounts/${accountId}/refresh`, { method: 'POST' });
      const data = res.data;
      if (data) {
        // Item archived/missing in 1P → local account is deactivated; the
        // toast is the primary feedback since the row will disappear from
        // the active list on reload.
        if (data.validation.status === 'archived') {
          toast.error('1Password item is archived', 'The account has been deactivated locally. Restore the item in 1Password and refresh again to reactivate.');
        } else if (data.validation.status === 'item_missing') {
          toast.error('1Password item not found', 'The account has been deactivated locally. The item may have been deleted.');
        } else {
          const fieldLine = `Detected fields: ${data.detected_fields.join(', ') || 'none'}`;
          let validationLine = '';
          switch (data.validation.status) {
            case 'validated':
              validationLine = `Live API check: PASSED (${data.validation.message})`;
              break;
            case 'failed':
              validationLine = `Live API check: FAILED — ${data.validation.message}`;
              break;
            case 'skipped_no_fields':
              validationLine = 'Live API check: skipped (required fields missing)';
              break;
            case 'skipped_no_adapter':
              validationLine = 'Live API check: skipped (registrar has no API adapter)';
              break;
          }
          const body = `${fieldLine}\n${validationLine}\nAPI Ready: ${data.api_ready ? 'Yes' : 'No'}`;
          if (data.validation.status === 'failed') toast.error('Refreshed from 1Password', body);
          else toast.success('Refreshed from 1Password', body);
        }
      }
      loadAccounts();
      if (selectedAccount?.id === accountId) {
        const accountRes = await fetchApi<ApiResponse<RegistrarAccount>>(`/accounts/${accountId}`);
        if (accountRes.data) setSelectedAccount(accountRes.data);
      }
    } catch (error) {
      toast.error('Refresh failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setRefreshing(null);
    }
  };

  const selectAccount = async (account: RegistrarAccount) => {
    setSelectedAccount(account);
    if (account.has_api) {
      loadSyncLogs(account.id);
    }
  };

  // 1Password functions
  async function loadOnePasswordStatus() {
    try {
      const res = await fetchApi<ApiResponse<OnePasswordStatus>>('/1password/status');
      if (res.data) setOpStatus(res.data);
    } catch (error) {
      console.error('Failed to load 1Password status:', error);
    }
  }

  const runScan = useCallback(async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await fetchApi<ApiResponse<ScanResult>>('/1password/scan', {
        method: 'POST',
      });
      if (res.data) {
        setScanResult(res.data);
        setExpandedRegistrars(new Set(Object.keys(res.data.grouped)));
        // Reload accounts if any were updated (api_ready status changed)
        if (res.data.accountsUpdated && res.data.accountsUpdated > 0) {
          loadAccounts();
        }
      }
    } catch (error) {
      console.error('Failed to scan:', error);
    } finally {
      setScanning(false);
    }
  }, [fetchApi]);

  const toggleRegistrar = (registrar: string) => {
    setExpandedRegistrars((prev) => {
      const next = new Set(prev);
      if (next.has(registrar)) {
        next.delete(registrar);
      } else {
        next.add(registrar);
      }
      return next;
    });
  };

  const handleImportItem = async (item: DiscoveredRegistrarItem, label?: string) => {
    if (!item.matchedRegistrarType) return;

    setLinking(`${item.vaultId}:${item.itemId}`);
    try {
      await fetchApi('/1password/link', {
        method: 'POST',
        body: JSON.stringify({
          vault_id: item.vaultId,
          item_id: item.itemId,
          registrar_type: item.matchedRegistrarType,
          ...(label ? { label } : {}),
        }),
      });
      // Refresh accounts and scan results
      loadAccounts();
      runScan();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      // The label auto-derived from the item collides with an existing account.
      // Let the user pick a different one and retry instead of dead-ending.
      if (/already exists/i.test(message)) {
        const suggested = item.itemTitle || item.usernameHint || '';
        const newLabel = window.prompt(`${message}\n\nEnter a different label for this account:`, suggested);
        if (newLabel && newLabel.trim()) {
          setLinking(null);
          await handleImportItem(item, newLabel.trim());
          return;
        }
      }
      toast.error('Import failed', message);
    } finally {
      setLinking(null);
    }
  };

  const handleImportAll = async () => {
    if (!scanResult) return;

    const unimportedItems = Object.values(scanResult.grouped)
      .flat()
      .filter(item => !item.isLinked && item.matchedRegistrarType);

    if (unimportedItems.length === 0) {
      toast.info('Nothing to import', 'All items are already imported.');
      return;
    }

    const apiReadyCount = unimportedItems.filter(i => i.hasApiCredentials).length;
    const loginOnlyCount = unimportedItems.length - apiReadyCount;

    const ok = await confirm({
      title: `Import ${unimportedItems.length} registrar account(s)?`,
      message: (
        <ul className="list-disc pl-5 space-y-1">
          <li>{apiReadyCount} with API credentials (ready for sync)</li>
          <li>{loginOnlyCount} login-only (manual entry or add API fields in 1Password)</li>
        </ul>
      ),
      confirmLabel: 'Import',
    });
    if (!ok) {
      return;
    }

    setScanning(true);
    let imported = 0;
    let failed = 0;

    for (const item of unimportedItems) {
      try {
        await fetchApi('/1password/link', {
          method: 'POST',
          body: JSON.stringify({
            vault_id: item.vaultId,
            item_id: item.itemId,
            registrar_type: item.matchedRegistrarType,
          }),
        });
        imported++;
      } catch {
        failed++;
      }
    }

    if (failed > 0) toast.error(`Imported ${imported} account(s)`, `${failed} failed`);
    else toast.success(`Imported ${imported} account(s)`);
    loadAccounts();
    runScan();
    setScanning(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Registrar Accounts" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2"><SkeletonCard lines={8} /></div>
          <SkeletonCard lines={4} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
      <PageHeader
        title="Registrar Accounts"
        actions={<>
          <button
            onClick={handleSyncAll}
            disabled={syncingAll}
            className="btn btn-secondary"
          >
            {syncingAll ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            {syncingAll ? 'Syncing...' : 'Sync All'}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Account
          </button>
        </>}
      />
      </div>

      {/* 1Password Import Section */}
      {opStatus?.connected && (
        <div className="card mb-6">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Import from 1Password</h2>
              <p className="text-sm text-slate-500">
                Scan your vaults to discover registrar accounts
              </p>
            </div>
            <div className="flex gap-2">
              {scanResult && Object.values(scanResult.grouped).flat().some(i => !i.isLinked) && (
                <button
                  onClick={handleImportAll}
                  disabled={scanning}
                  className="btn btn-primary"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Import All ({Object.values(scanResult.grouped).flat().filter(i => !i.isLinked).length})
                </button>
              )}
              <button
                onClick={runScan}
                disabled={scanning}
                className="btn btn-secondary"
              >
                {scanning ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                {scanning ? 'Scanning...' : 'Scan Vaults'}
              </button>
            </div>
          </div>

          {scanResult && (
            <div className="p-6">
              {/* Summary */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg mb-4">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">
                    Found {scanResult.total} registrar accounts across {Object.keys(scanResult.grouped).length} registrars
                  </p>
                  <p>
                    <span className="text-green-700 font-medium">
                      {Object.values(scanResult.grouped).flat().filter(i => i.hasApiCredentials).length} API-ready
                    </span>
                    {' · '}
                    <span className="text-yellow-700">
                      {Object.values(scanResult.grouped).flat().filter(i => !i.hasApiCredentials).length} login-only
                    </span>
                    {scanResult.accountsUpdated && scanResult.accountsUpdated > 0 && (
                      <>
                        {' · '}
                        <span className="text-brand-700 font-medium">
                          {scanResult.accountsUpdated} account{scanResult.accountsUpdated > 1 ? 's' : ''} updated
                        </span>
                      </>
                    )}
                  </p>
                  <p className="text-xs mt-1 text-blue-600">
                    To enable API sync, add fields like "api_key", "api_secret" to items in 1Password.
                  </p>
                </div>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {Object.entries(scanResult.grouped)
                  .sort(([, a], [, b]) => b.length - a.length)
                  .map(([registrar, items]) => {
                    const required = REQUIRED_FIELDS[registrar] || [];
                    const apiReadyCount = items.filter(i => i.hasApiCredentials).length;

                    return (
                      <div key={registrar} className="border border-slate-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleRegistrar(registrar)}
                          className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {expandedRegistrars.has(registrar) ? (
                              <ChevronDown className="h-4 w-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-slate-500" />
                            )}
                            <span className="font-medium text-slate-900">{registrarName(registrar)}</span>
                            <span className="text-sm text-slate-500">({items.length})</span>
                            {apiReadyCount > 0 && (
                              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                                {apiReadyCount} API-ready
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-slate-500">
                            {items.filter((i) => i.isLinked).length} imported
                          </span>
                        </button>

                        {expandedRegistrars.has(registrar) && (
                          <>
                            {required.length > 0 && (
                              <div className="px-4 py-2 bg-slate-100 border-b border-slate-200">
                                <p className="text-xs text-slate-600">
                                  Required 1Password fields for API: <span className="font-mono">{required.join(', ')}</span>
                                </p>
                              </div>
                            )}
                            <div className="divide-y divide-slate-100">
                              {items.map((item) => (
                                <div
                                  key={`${item.vaultId}-${item.itemId}`}
                                  className="px-4 py-3 flex items-center justify-between hover:bg-slate-50"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-medium text-slate-900 truncate">
                                        {item.itemTitle}
                                      </p>
                                      {item.hasApiCredentials ? (
                                        <span className="inline-flex items-center text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                                          API
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                                          Login
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-500 truncate">
                                      {item.vaultName}
                                      {item.usernameHint && ` · ${item.usernameHint}`}
                                      {item.detectedFields.length > 0 && (
                                        <span className="ml-1 text-slate-400">
                                          ({item.detectedFields.join(', ')})
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                  <div className="ml-4 flex items-center gap-2">
                                    {item.isLinked ? (
                                      <span className="inline-flex items-center text-xs text-emerald-600">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        Imported
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => handleImportItem(item)}
                                        disabled={linking === `${item.vaultId}:${item.itemId}`}
                                        className="btn btn-secondary text-xs py-1 px-2"
                                      >
                                        {linking === `${item.vaultId}:${item.itemId}` ? (
                                          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                        ) : (
                                          <Download className="h-3 w-3 mr-1" />
                                        )}
                                        Import
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Accounts list */}
        <div className="lg:col-span-2">
          <div className="card overflow-hidden">
            {accounts.length === 0 ? (
              <EmptyState
                icon={Building2}
                title="No accounts configured yet"
                description="Connect a registrar to start syncing its domains."
                action={
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="btn btn-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Account
                  </button>
                }
              />
            ) : (
              <>
                {/* Filter input */}
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                  <div className="relative">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Filter accounts..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      className="w-full pl-9 pr-8 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                    {filterText && (
                      <button
                        onClick={() => setFilterText('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {filterText && (
                    <p className="text-xs text-slate-500 mt-1">
                      Showing {filteredAccounts.length} of {accounts.length} accounts
                    </p>
                  )}
                </div>
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="table-header">Account</th>
                      <th className="table-header">API</th>
                      <th className="table-header">Login</th>
                      <th className="table-header">Domains</th>
                      <th className="table-header">Credentials</th>
                      <th className="table-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {groupedAccounts.map((group) => (
                    <Fragment key={group.type}>
                      <tr className="bg-slate-100/80 sticky top-0">
                        <td colSpan={6} className="px-4 py-2.5">
                          <span className="flex items-center gap-2.5">
                            {logoUrlFor(group.type) && (
                              <img
                                src={logoUrlFor(group.type)!}
                                alt=""
                                width={20}
                                height={20}
                                style={{ borderRadius: 4, flexShrink: 0 }}
                                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                            <span className="font-semibold text-slate-800 text-sm">
                              {group.name}
                            </span>
                            <span className="text-xs text-slate-500">
                              {group.accounts.length} account{group.accounts.length === 1 ? '' : 's'}
                            </span>
                          </span>
                        </td>
                      </tr>
                      {group.accounts.map((account) => (
                    <tr
                      key={account.id}
                      className={`hover:bg-slate-50 cursor-pointer ${
                        selectedAccount?.id === account.id ? 'bg-brand-50' : ''
                      }`}
                      onClick={() => selectAccount(account)}
                    >
                      <td className="table-cell font-medium text-slate-900">
                        <span className="flex items-center gap-2">
                          {(() => {
                            const lastSync = syncStatus[account.id];
                            if (!lastSync) return (
                              <span className="w-2.5 h-2.5 rounded-full bg-slate-300 flex-shrink-0" title="Never synced" />
                            );
                            if (lastSync.status === 'completed') return (
                              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" title={`Last sync: ${lastSync.completed_at || lastSync.started_at}`} />
                            );
                            if (lastSync.status === 'failed') return (
                              <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" title={`Failed: ${lastSync.error_message || 'Unknown error'}`} />
                            );
                            return (
                              <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" title="Syncing..." />
                            );
                          })()}
                          <span className="min-w-0">
                            {account.display_name ?? account.label}
                            {account.display_name && <span className="ml-1.5 font-mono text-[11px] font-normal text-slate-400">{account.label}</span>}
                          </span>
                          {account.credential_source === '1password' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                              <KeyRound className="h-3 w-3 mr-0.5" />
                              1P
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="table-cell">
                        {account.has_api ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                            {account.api_type || 'REST'}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">None</span>
                        )}
                      </td>
                      <td className="table-cell text-slate-500 font-mono text-xs">
                        {account.username_hint || '—'}
                      </td>
                      <td className="table-cell text-slate-500">
                        {account.domain_count || 0}
                      </td>
                      <td className="table-cell">
                        {!account.has_api ? (
                          <span className="text-slate-400">N/A</span>
                        ) : account.credential_source === '1password' ? (
                          account.api_ready ? (
                            <span className="inline-flex items-center text-emerald-600">
                              <Key className="h-4 w-4 mr-1" />
                              API Ready
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-yellow-600" title="Add API fields in 1Password">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              Login Only
                            </span>
                          )
                        ) : account.has_credentials ? (
                          <span className="inline-flex items-center text-emerald-600">
                            <Key className="h-4 w-4 mr-1" />
                            Set
                          </span>
                        ) : (
                          <span className="text-yellow-600">Not set</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="flex gap-2">
                          {account.has_api && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTest(account.id);
                                }}
                                disabled={
                                  (account.credential_source === '1password' ? !account.api_ready : !account.has_credentials) ||
                                  testing === account.id
                                }
                                className="text-slate-500 hover:text-brand-600 disabled:opacity-50"
                                title="Test connection"
                              >
                                {testing === account.id ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <AlertCircle className="h-4 w-4" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSync(account.id);
                                }}
                                disabled={
                                  (account.credential_source === '1password' ? !account.api_ready : !account.has_credentials) ||
                                  syncing === account.id
                                }
                                className="text-slate-500 hover:text-brand-600 disabled:opacity-50"
                                title="Sync domains"
                              >
                                {syncing === account.id ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                              </button>
                            </>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(account.id);
                            }}
                            className="text-slate-500 hover:text-red-600"
                            title="Delete account"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    ))}
                    </Fragment>
                    ))}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Details panel */}
        <div>
          {selectedAccount ? (
            <div className="card">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">
                  {selectedAccount.display_name ?? selectedAccount.label}
                </h2>
                <p className="text-sm text-slate-500">
                  {selectedAccount.registrar_name} · login <code className="text-xs">{selectedAccount.label}</code>
                  {!selectedAccount.display_name && <> · <a href="/account-names" className="text-brand-600 hover:underline">set owner</a></>}
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <p className="text-sm text-slate-500">Domains</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {selectedAccount.domain_count || 0}
                  </p>
                </div>

                {selectedAccount.username_hint && (
                  <div>
                    <p className="text-sm text-slate-500">Login</p>
                    <p className="text-slate-900 font-mono text-sm">
                      {selectedAccount.username_hint}
                    </p>
                  </div>
                )}

                {selectedAccount.has_api && (
                  <>
                    <div>
                      <p className="text-sm text-slate-500">API Type</p>
                      <p className="text-slate-900">
                        {selectedAccount.api_type?.toUpperCase() || 'Unknown'}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-slate-500">Credentials</p>
                      <p className="text-slate-900">
                        {selectedAccount.credential_source === '1password' ? (
                          selectedAccount.api_ready ? (
                            <span className="inline-flex items-center text-emerald-600">
                              <KeyRound className="h-4 w-4 mr-1" />
                              1Password (API Ready)
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-yellow-600">
                              <KeyRound className="h-4 w-4 mr-1" />
                              1Password (Login Only)
                            </span>
                          )
                        ) : selectedAccount.has_credentials ? (
                          <span className="text-emerald-600">Local (encrypted)</span>
                        ) : (
                          <span className="text-yellow-600">Not configured</span>
                        )}
                      </p>
                    </div>

                    {selectedAccount.credential_source === '1password' && !selectedAccount.api_ready && (
                      <div className="p-3 bg-yellow-50 rounded-lg">
                        <p className="text-sm text-yellow-700">
                          This account only has login credentials. To enable API sync, add fields like{' '}
                          <span className="font-mono text-xs">api_key</span>,{' '}
                          <span className="font-mono text-xs">api_secret</span> to the 1Password item.
                        </p>
                      </div>
                    )}

                    {selectedAccount.credential_source === '1password' ? (
                      <div className="space-y-2">
                        <button
                          onClick={() => handleRefresh1Password(selectedAccount.id)}
                          disabled={refreshing === selectedAccount.id}
                          className="btn btn-primary w-full"
                        >
                          {refreshing === selectedAccount.id ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <RotateCcw className="h-4 w-4 mr-2" />
                          )}
                          Refresh from 1Password
                        </button>
                        <button
                          onClick={() => handleUnlink(selectedAccount.id)}
                          className="btn btn-secondary w-full"
                        >
                          <Unlink className="h-4 w-4 mr-2" />
                          Unlink from 1Password
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowCredentialsModal(true)}
                        className="btn btn-secondary w-full"
                      >
                        <Key className="h-4 w-4 mr-2" />
                        {selectedAccount.has_credentials
                          ? 'Update Credentials'
                          : 'Set Credentials'}
                      </button>
                    )}

                    {selectedAccount.registrar_type === 'safenames' && (
                      <button
                        onClick={() => handleSafenamesProbe(selectedAccount.id)}
                        disabled={testing === selectedAccount.id}
                        className="btn btn-secondary w-full"
                      >
                        <Bug className="h-4 w-4 mr-2" />
                        Debug API (token probe)
                      </button>
                    )}
                  </>
                )}

                {!selectedAccount.has_api && (
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-700">
                      This registrar does not have an API. Use CSV import to add
                      domains manually.
                    </p>
                  </div>
                )}

                {/* Sync logs */}
                {selectedAccount.has_api && syncLogs.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Recent Syncs</p>
                    <div className="space-y-2">
                      {syncLogs.slice(0, 5).map((log) => (
                        <div
                          key={log.id}
                          className="p-2 bg-slate-50 rounded text-sm"
                        >
                          <div className="flex justify-between">
                            <span
                              className={
                                log.status === 'completed'
                                  ? 'text-emerald-600'
                                  : log.status === 'failed'
                                  ? 'text-red-600'
                                  : 'text-yellow-600'
                              }
                            >
                              {log.status}
                            </span>
                            <span className="text-slate-500">
                              {new Date(log.started_at).toLocaleDateString()}
                            </span>
                          </div>
                          {log.status === 'completed' && (
                            <p className="text-slate-600">
                              {log.domains_synced} synced ({log.domains_added}{' '}
                              added, {log.domains_updated} updated)
                            </p>
                          )}
                          {log.error_message && (
                            <p className="text-red-600 text-xs">
                              {log.error_message}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card p-6 text-center text-slate-500">
              Select an account to view details
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <AddAccountModal
          registrarTypes={registrarTypes}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadAccounts();
          }}
        />
      )}

      {showCredentialsModal && selectedAccount && (
        <CredentialsModal
          account={selectedAccount}
          registrarTypes={registrarTypes}
          onClose={() => setShowCredentialsModal(false)}
          onSuccess={() => {
            setShowCredentialsModal(false);
            loadAccounts();
          }}
        />
      )}
    </div>
  );
}

function AddAccountModal({
  registrarTypes,
  onClose,
  onSuccess,
}: {
  registrarTypes: RegistrarType[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { fetchApi } = useApi();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [credentials, setCredentials] = useState<Record<string, string>>({});

  const selectedRegistrar = registrarTypes.find((r) => r.id === selectedType);
  const fields = selectedRegistrar?.requiredCredentials || [];
  const optionalFields = selectedRegistrar?.optionalCredentials || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetchApi('/accounts', {
        method: 'POST',
        body: JSON.stringify({
          registrar_type: selectedType,
          credentials,
        }),
      });
      onSuccess();
    } catch (error) {
      toast.error('Failed to create account', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Add Registrar Account"
      onClose={onClose}
      footer={<>
        <button type="button" onClick={onClose} className="btn btn-secondary">
          Cancel
        </button>
        <button
          type="submit"
          form="add-account-form"
          disabled={loading || !selectedType || fields.some((f) => !credentials[f])}
          className="btn btn-primary"
        >
          {loading ? 'Creating...' : 'Create Account'}
        </button>
      </>}
    >
        <form id="add-account-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="registrarType" className="label">
                Registrar Type
              </label>
              <select
                id="registrarType"
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setCredentials({});
                }}
                className="input"
                required
              >
                <option value="">Select a registrar</option>
                {registrarTypes
                  .filter((r) => r.hasApi)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
              </select>
            </div>

            {selectedType && fields.length > 0 && (
              <>
                {fields.map((field) => (
                  <div key={field}>
                    <label htmlFor={field} className="label">
                      {field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </label>
                    <input
                      id={field}
                      type={field.includes('password') || field.includes('secret') ? 'password' : 'text'}
                      value={credentials[field] || ''}
                      onChange={(e) =>
                        setCredentials({ ...credentials, [field]: e.target.value })
                      }
                      className="input"
                      required
                    />
                  </div>
                ))}
                {optionalFields.map((field) => (
                  <div key={field}>
                    <label htmlFor={field} className="label">
                      {field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input
                      id={field}
                      type="text"
                      value={credentials[field] || ''}
                      onChange={(e) =>
                        setCredentials({ ...credentials, [field]: e.target.value })
                      }
                      className="input"
                    />
                    {OPTIONAL_FIELD_HINTS[field] && <p className="text-xs text-slate-500 mt-1">{OPTIONAL_FIELD_HINTS[field]}</p>}
                  </div>
                ))}

                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    The account label will be automatically set from the{' '}
                    {fields.includes('username') ? 'username' : 'first 8 characters of the API key'}.
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-700">
              Credentials are encrypted before storage. Never share your API keys.
            </p>
          </div>
        </form>
    </Modal>
  );
}

function CredentialsModal({
  account,
  registrarTypes,
  onClose,
  onSuccess,
}: {
  account: RegistrarAccount;
  registrarTypes: RegistrarType[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { fetchApi } = useApi();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState<Record<string, string>>({});

  const registrar = registrarTypes.find((r) => r.id === account.registrar_type);
  const fields = registrar?.requiredCredentials || ['api_key'];
  const optionalFields = registrar?.optionalCredentials || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetchApi(`/accounts/${account.id}`, {
        method: 'PUT',
        body: JSON.stringify({ credentials }),
      });
      onSuccess();
    } catch (error) {
      toast.error('Failed to save credentials', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`${account.display_name ?? account.label} Credentials`}
      onClose={onClose}
      footer={<>
        <button type="button" onClick={onClose} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" form="credentials-form" disabled={loading} className="btn btn-primary">
          {loading ? 'Saving...' : 'Save Credentials'}
        </button>
      </>}
    >
        <form id="credentials-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {fields.map((field) => (
              <div key={field}>
                <label htmlFor={field} className="label">
                  {field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </label>
                <input
                  id={field}
                  type={field.includes('password') || field.includes('secret') ? 'password' : 'text'}
                  value={credentials[field] || ''}
                  onChange={(e) =>
                    setCredentials({ ...credentials, [field]: e.target.value })
                  }
                  className="input"
                  required
                />
              </div>
            ))}
            {optionalFields.map((field) => (
              <div key={field}>
                <label htmlFor={field} className="label">
                  {field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  id={field}
                  type="text"
                  value={credentials[field] || ''}
                  onChange={(e) =>
                    setCredentials({ ...credentials, [field]: e.target.value })
                  }
                  className="input"
                />
                {OPTIONAL_FIELD_HINTS[field] && <p className="text-xs text-slate-500 mt-1">{OPTIONAL_FIELD_HINTS[field]}</p>}
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-700">
              Credentials are encrypted before storage. Never share your API keys.
            </p>
          </div>
        </form>
    </Modal>
  );
}
